import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import classes from './TasksFiltersV2.module.css'
import InputBox from "../common/interactive/InputBox"

import CustomSelect from "../common/interactive/CustomSelect"
import CustomSelectMultiple from "../common/interactive/CustomSelectMultiple"
import Button from "../common/Button"
import { tasksActions } from "../../store/tasks"
import CheckboxCell from "../common/interactive/CheckboxCell"
import { noteActionsOptions, noteTypesOptions, teamsOptions } from "../../lib/systemData"
import { canUseNoteTypeId, filterNoteTypesByPermission } from "../../lib/notesPermissions"
import { getItemFromStorage, teamAllowsTasks } from "../../lib/script"
import { setFiltersBasedOnWorkQ } from "../../lib/utils"
import { exportTasks } from "../../lib/fetchApi"
import { notificationActions } from "../../store/notifications"
import TasksRefreshApi from "./TasksRefreshApi"
import BodySimpleModalV3 from "../common/modal/BodySimpleModalV3"

export default function TasksFiltersV2() {
    const dispatch = useDispatch()
    const selectedFilters = useSelector(state => state.tasks.filters)
    const orderFilters = useSelector(state => state.tasks.orderFilters)
    const userToken = useSelector(state => state.user.token)
    const editPermissions = useSelector(state => state.permissions.editPermissions)
    const permittedNoteTypes = useMemo(
        () => filterNoteTypesByPermission(noteTypesOptions() || [], editPermissions),
        [editPermissions]
    )

    const [filtersValues, setFiltersValues] = useState(null)
    const [exportLoading, setExportLoading] = useState(false)

    const [noteActionsValue, setNoteActionsValue] = useState(noteActionsOptions())
    const allTeamsOptions = useMemo(() => {
        const sourceTeams = teamsOptions()
        if (!sourceTeams || !sourceTeams.length) {
            return null
        }

        const user = getItemFromStorage('user')
        const allowedTeamIds = new Set(
            (user?.teams || [])
                .filter((team) => teamAllowsTasks(team))
                .map((team) => String(team.id))
        )

        const flattenTeams = (teamsList, level = 0) => {
            return teamsList.flatMap((team) => {
                const prefix = level > 0 ? `${'- '.repeat(level)}` : ''
                const currentTeam = {
                    label: `${prefix}${team.name}`,
                    value: team.id,
                    ...team
                }

                const children = team.children?.length ? flattenTeams(team.children, level + 1) : []
                return [currentTeam, ...children]
            })
        }

        return flattenTeams(sourceTeams).filter((row) => allowedTeamIds.has(String(row.id ?? row.value)))
    }, [])

    function mainSelectAction(value) {
        let update = {
            ...filtersValues,
        }
        if (value.identifier.type == 'note_type_id') {
            if (update.note_action_id) {
                update.note_action_id = null
            }

            if (noteActionsOptions()) {
                let filteredOptions = noteActionsOptions().filter(item => item.note_type_id == value.data.value)

                setNoteActionsValue(filteredOptions)
            }
        }

        

        //need to expand the object and include name of the selected options!
        if (value.data.value.constructor == Array) { // check if array
            update[value.identifier.type] = value.options
        } else {
            update[value.identifier.type] = {
                value: value.data.value,
                item: value.option
            }
        }   

        if (value.identifier.type == 'show_resolved') {
            if (value.data.value) {
                update.show_resolved = {
                    value: value.data.value,
                    item: value.option
                }
            } else {
                delete update.show_resolved
            }
        }

        if (value.identifier.type == 'action_needed') {
            if (!value.data.value || value.data.value === 'all') {
                delete update.action_needed
            }
        }

        setFiltersValues(update)

        // on change set filters....
        dispatch(tasksActions.setCurrentPage(1))
        dispatch(tasksActions.setFiltersMultiple(update))
    }

    function clearAllFiltersHandler() {
        dispatch(tasksActions.setFiltersMultiple({}))   
    }

    async function exportHandler() {
        setExportLoading(true)
        const filtersString = setFiltersBasedOnWorkQ(selectedFilters)
        const orderString = orderFilters
            ? `&sort_by=${orderFilters.label}&sort_direction=${orderFilters.isDesc ? 'desc' : 'asc'}`
            : null

        const response = await exportTasks(userToken, filtersString, orderString)

        if (response && response.errorMessage) {
            dispatch(notificationActions.setError(response))
        } else if (response) {
            const today = new Date()
            const mm = String(today.getMonth() + 1).padStart(2, '0')
            const dd = String(today.getDate()).padStart(2, '0')
            const yyyy = today.getFullYear()
            const fileURL = URL.createObjectURL(response)
            const a = document.createElement('a')
            a.href = fileURL
            a.download = `tasks-export-${yyyy}-${mm}-${dd}.xlsx`
            a.click()
            URL.revokeObjectURL(fileURL)
        }
        setExportLoading(false)
    }

    // set the filters from settings 
    useEffect(() => {
        let mergeTheSelectedFilters = {
            ...selectedFilters
        }
        if (
            mergeTheSelectedFilters?.note_type_id?.value != null
            && !canUseNoteTypeId(mergeTheSelectedFilters.note_type_id.value, editPermissions)
        ) {
            delete mergeTheSelectedFilters.note_type_id
            if (mergeTheSelectedFilters.note_action_id) {
                delete mergeTheSelectedFilters.note_action_id
            }
            dispatch(tasksActions.setFiltersMultiple(mergeTheSelectedFilters))
        }
        setFiltersValues(mergeTheSelectedFilters)        
    }, [selectedFilters, editPermissions, dispatch])  
    

    return <div className={classes.master_filter}>

        <CheckboxCell label={'Show Resolved'} checked={filtersValues?.show_resolved?.value} action={mainSelectAction} direction={'reverse'} identifier={{type: 'show_resolved', label: 'Show Resolved'}}/>
        <InputBox label={'Search'} action={mainSelectAction} name={'search'} currentValue={filtersValues?.search?.value} identifier={{type: 'search', label: 'Search'}} setStyle={'bk_white'}/>
        <CustomSelect label={"Note Type"} optionsData={permittedNoteTypes} preSelectedValue={filtersValues?.note_type_id?.value} action={mainSelectAction} name={'note_type_id'} identifier={{type: 'note_type_id', label: 'Note Type'}}/>
        <CustomSelectMultiple
            label={"Teams"}
            optionsData={allTeamsOptions}
            preSelectedValue={filtersValues?.team_ids?.map(item => item.value)}
            action={mainSelectAction}
            name={'team_ids'}
            identifier={{type: 'team_ids', label: 'Teams'}}
        />

        <CustomSelect label={"Actions"} optionsData={noteActionsValue} preSelectedValue={filtersValues?.note_action_id?.value} action={mainSelectAction} name={'note_action_id'} identifier={{type: 'note_action_id', label: 'Actions'}}/>
        <CustomSelect
            label={"Action needed"}
            optionsData={[
                { label: 'All', value: 'all' },
                { label: 'Needs Resolve', value: 'resolve' },
                { label: 'Needs Complete', value: 'complete' },
            ]}
            preSelectedValue={filtersValues?.action_needed?.value ?? 'all'}
            action={mainSelectAction}
            name={'action_needed'}
            identifier={{type: 'action_needed', label: 'Action needed'}}
        />

        <Button click={clearAllFiltersHandler} btnStyle={'btn_white'}>Clear</Button>
        <BodySimpleModalV3 message={'Export tasks to Excel'}>
            <Button click={exportHandler} btnStyle={'btn_white'} isLoading={exportLoading} title={'Export'}>
                <i className="ti ti-file-arrow-right"></i>
            </Button>
        </BodySimpleModalV3>
        <TasksRefreshApi />
    </div>
}
